import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type FallbackCandidateContext,
  useDisagreementReviewSession,
} from '../../hooks/useDisagreementReviewSession';
import { fetchDisagreementCandidates, fetchUsers } from '../../services/api';
import {
  type Criteria,
  type DisagreementCandidate,
  EVAL_JUDGEMENT_SOURCE,
  type MarkingMetricsRow,
  REVIEWED_CLASSIFICATIONS,
  type ReviewedClassification,
  type User,
} from '../../types';
import EssayContent from '../EssayContent';
import DisagreementCandidateSummary from './DisagreementCandidateSummary';
import { formatPct } from './MarkingMetrics';

export interface TranscriptComparisonDialogProps {
  open: boolean;
  row: MarkingMetricsRow | null;
  onClose: () => void;
  users?: User[];
}

export default function TranscriptComparisonDialog({
  open,
  row,
  onClose,
  users: initialUsers,
}: TranscriptComparisonDialogProps) {
  const [users, setUsers] = useState<User[]>(() => initialUsers ?? []);

  useEffect(() => {
    if (initialUsers && initialUsers.length > 0) {
      setUsers(initialUsers);
      return;
    }
    if (!open) return;
    let cancelled = false;
    fetchUsers()
      .then(fetched => {
        if (!cancelled) setUsers(fetched);
      })
      .catch(err => {
        console.error('Failed to load users for comparison dialog:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, initialUsers]);

  const usersById = useMemo(() => {
    const map: Record<string, User> = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);

  const reviewerName = useCallback(
    (userId: string) => {
      const u = usersById[userId];
      return u ? `${u.firstName} ${u.lastName}` : userId.slice(0, 8);
    },
    [usersById]
  );

  const criteriaList = useMemo<Criteria[]>(() => {
    if (!row) return [];
    return [
      {
        id: row.id,
        name: row.name,
        granularity: row.granularity,
        creationDate: '',
        modifiedDate: '',
        policyId: null,
      },
    ];
  }, [row]);

  const createFallbackCandidate = useCallback(
    ({
      transcriptionId,
      fragment,
      index,
      judgement,
      paperNumber,
    }: FallbackCandidateContext): DisagreementCandidate => {
      const entity = judgement?.entities?.[index] ?? judgement?.sentences?.[index];
      const aiLabel = (entity?.label ?? 'no attempt') as ReviewedClassification;
      return {
        transcriptionId,
        paperNumber: paperNumber ?? 0,
        sentence: fragment,
        fragmentIndex: index,
        granularity: judgement?.granularity ?? 'sentence',
        humanLabel: 'no attempt',
        humanReviewerCount: 0,
        humanReasons: [],
        aiLabel,
        aiReason: entity?.reason || '',
        groundTruthSource: 'label_consensus',
        previousReview: null,
        alreadyReviewed: false,
        review: null,
        hasDisagreement: false,
      };
    },
    []
  );

  const fetchCandidates = useCallback(
    (criteriaId: string) =>
      fetchDisagreementCandidates(criteriaId, 'inclusive', true, EVAL_JUDGEMENT_SOURCE, true),
    []
  );

  const session = useDisagreementReviewSession<DisagreementCandidate>({
    selectedCriteriaId: row?.id ?? '',
    criteriaList,
    enabled: open && Boolean(row),
    fetchCandidates,
    judgementSource: EVAL_JUDGEMENT_SOURCE,
    includeAllTranscriptions: true,
    onCandidatesError: () => {},
    onSubmitError: () => {},
    getTranscriptionId: c => c.transcriptionId,
    getEntityText: c => c.sentence,
    getPaperNumber: c => c.paperNumber,
    getDisplayLabel: c => c.humanLabel,
    getAlreadyReviewed: c => c.alreadyReviewed,
    getReview: c => c.review,
    getOriginalForSubmit: c => c.aiLabel,
    applyOptimisticReview: (c, review) => ({ ...c, alreadyReviewed: true, review }),
    getHasDisagreement: c => c.hasDisagreement !== false,
    createFallbackCandidate,
  });

  const {
    loadingCandidates,
    selectedTranscriptionId,
    setSelectedTranscriptionId,
    judgement,
    loadingJudgement,
    selectedFragmentIndex,
    editType,
    setEditType,
    editReason,
    setEditReason,
    submitting,
    transcriptionOptions,
    fragments,
    selectedCandidate,
    handleFragmentClick,
    getLabelTypeForFragment,
    isFragmentReviewed,
    handleSubmit,
    goToPreviousTranscription,
    goToNextTranscription,
    goToPreviousLabel,
    goToNextLabel,
  } = session;

  const selectedGranularity = judgement?.granularity ?? row?.granularity ?? 'sentence';

  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="div">
            Transcript comparison: {row.name}
          </Typography>
          <Chip size="small" label={row.granularity} variant="outlined" />
          {row.exactAgreement !== null && (
            <Chip
              size="small"
              color={row.performancePasses ? 'success' : 'default'}
              label={`Exact agreement: ${formatPct(row.exactAgreement)}`}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {row.comparedCount} compared {row.comparedCount === 1 ? 'case' : 'cases'}
          </Typography>
        </Box>
        <IconButton aria-label="Close dialog" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          px: 2.5,
          pb: 2.5,
          pt: 3,
          gap: 2,
        }}
      >
        {/* Top Controls: Paper selection & entity navigation */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            flexShrink: 0,
            mt: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="transcript-select-label">Select paper</InputLabel>
              <Select
                labelId="transcript-select-label"
                label="Select paper"
                value={selectedTranscriptionId}
                onChange={e => setSelectedTranscriptionId(e.target.value as string)}
                disabled={loadingCandidates || transcriptionOptions.length === 0}
              >
                {transcriptionOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Previous paper">
              <span>
                <IconButton
                  size="small"
                  onClick={goToPreviousTranscription}
                  disabled={transcriptionOptions.length <= 1}
                  aria-label="Previous paper"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next paper">
              <span>
                <IconButton
                  size="small"
                  onClick={goToNextTranscription}
                  disabled={transcriptionOptions.length <= 1}
                  aria-label="Next paper"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={goToPreviousLabel}
              disabled={fragments.length === 0}
            >
              Previous entity
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              onClick={goToNextLabel}
              disabled={fragments.length === 0}
            >
              Next entity
            </Button>
          </Box>
        </Box>

        {/* Main Transcript Essay Viewer */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {loadingCandidates || loadingJudgement ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={32} />
            </Box>
          ) : transcriptionOptions.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                py: 4,
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 500 }}>
                No compared transcriptions found for this criterion. Either eval marks or ground
                truth labels are missing.
              </Alert>
            </Box>
          ) : fragments.length > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
                p: 2.5,
                position: 'relative',
              }}
            >
              <EssayContent
                fragments={fragments}
                selectedFragmentIndex={selectedFragmentIndex}
                handleFragmentClick={handleFragmentClick}
                getLabelTypeForFragment={getLabelTypeForFragment}
                isFragmentReviewed={isFragmentReviewed}
                granularity={selectedGranularity}
                emphasizeSelected
              />
            </Paper>
          ) : (
            <Typography color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
              No {selectedGranularity} entities returned for this transcription.
            </Typography>
          )}
        </Box>

        {/* Candidate Ground Truth vs AI mark Summary */}
        {selectedCandidate && (
          <Box sx={{ flexShrink: 0 }}>
            <DisagreementCandidateSummary
              candidate={selectedCandidate}
              reviewerName={reviewerName}
            />

            {/* If there is an unresolved disagreement, allow resolving directly */}
            {selectedCandidate.hasDisagreement !== false && (
              <Paper elevation={2} sx={{ p: 1.25, mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <Typography variant="subtitle2">Resolution</Typography>
                  {selectedCandidate.previousReview && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const review = selectedCandidate.previousReview!;
                        setEditType(review.correctedClassification);
                        setEditReason(review.reason);
                      }}
                    >
                      Copy from previous review
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 1.25,
                  }}
                >
                  <ToggleButtonGroup
                    value={editType}
                    exclusive
                    onChange={(_, val) => setEditType(val)}
                    size="small"
                  >
                    {REVIEWED_CLASSIFICATIONS.map(c => (
                      <ToggleButton
                        key={c}
                        value={c}
                        sx={{
                          fontWeight: 600,
                          px: 1.5,
                          textTransform: 'capitalize',
                        }}
                      >
                        {c}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>

                  <TextField
                    size="small"
                    placeholder="Reason for resolution..."
                    value={editReason}
                    onChange={e => setEditReason(e.target.value)}
                    sx={{ flex: 1 }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!editType || submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? 'Saving...' : 'Submit Resolution'}
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Button
          component="a"
          href={`/admin/disagreements/consensus-vs-ai?criteriaId=${row.id}${
            selectedTranscriptionId ? `&transcriptionId=${selectedTranscriptionId}` : ''
          }&ai_source=eval`}
          startIcon={<OpenInNewIcon />}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Disagreement Review
        </Button>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
